/* ==========================================================================
   Big Gun Exhaust: known fit across the shopping journey.
   Design: Strategy/2026-10/known-fit/Known-Fit-Design-2026-10-03.md

   With a machine saved in the Garage, lists of products lead with the parts
   that fit it, say so in words, and keep a Show all one tap away (remembered
   per surface for the session). Everything runs in the browser after load,
   from the Garage in localStorage: the server HTML and its links never change,
   and server-rendered cards are hidden (data-bge-kf-hidden), never removed.
   Zero guard: if nothing in a list fits, the list stays as it is, unlabelled.

   Feed: the Garage filter's index and card templates, through
   window.BGE.fitFeed (assets/bge-garage.js). No new mechanism.
   ========================================================================== */
(function () {
  'use strict';

  var ALL_KEY = 'bge_kf_all:';   // + surface: '1' = Show all, for the session

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) {
    try { if (v == null) window.sessionStorage.removeItem(k); else window.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ }
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  // GA4 through the store's Google channel (same nil-safe helper as biggun-v2.js).
  function track(name, params) {
    try { if (typeof window.gtag === 'function') window.gtag('event', name, params || {}); } catch (e) { /* ignore */ }
  }
  function plural(n, word) { return n === 1 ? word : word + 's'; }
  function fitVerb(n) { return n === 1 ? 'fits' : 'fit'; }

  // bge-garage.js is deferred ahead of this file; wait for its API anyway.
  function whenGarage(cb) {
    var tries = 0;
    (function poll() {
      var B = window.BGE;
      if (B && B.Garage && B.fitFeed && typeof B.Garage.list === 'function') return cb(B);
      if (++tries > 50) return;
      setTimeout(poll, 100);
    })();
  }

  var B = null; // window.BGE once ready
  function name(v) { return B.formatVehicle(v); }
  function modelName(v) { return v.model || v.make; }
  function joinOr(list) {
    if (list.length < 2) return list.join('');
    return list.slice(0, -1).join(', ') + ' or ' + list[list.length - 1];
  }
  function isAll(surface) { return ssGet(ALL_KEY + surface) === '1'; }
  function setAll(surface, on) { ssSet(ALL_KEY + surface, on ? '1' : null); }
  function shopUrl(v) { return B.browseUrl(v); }
  function shopLabel(v) { return 'Shop ' + v.make + (v.model ? ' ' + v.model : '') + ' parts'; }

  // What a list depends on: the machines, their links (model collections load
  // late) and Show all. The Garage emits change more than once on load.
  function stateSig(vs, surface) {
    return JSON.stringify([vs.map(function (v) { return [v.id, v.year, shopUrl(v)]; }), isAll(surface)]);
  }

  var parsedCache = {};
  function parsed(row) {
    var id = row[0];
    if (!parsedCache[id]) parsedCache[id] = B.parseTags(row[2]);
    return parsedCache[id];
  }

  // Parts that fit by pipe, not by machine (cores, quiet inserts, packing kits): no machine tags plus the
  // Replacement Parts tag, the fit bar's rule (bgv2-pdp-fitbar.liquid). The Garage reads the cores as
  // untagged because their product type (Slip On Mufflers) is not a universal type (FL1-30).
  var BY_PIPE_RE = /(^|\|)\s*replacement parts\s*(\||$)/i;
  function byPipe(state, tagsStr) { return state === 'untagged' && BY_PIPE_RE.test(tagsStr || '') ? 'universal' : state; }

  // Rows [id, handle, tags, type, available] -> what fits the saved machines.
  // fits: [{ row, vehicle }] in list order; universal: rows; counts per machine.
  function match(rows, vehicles, opts) {
    opts = opts || {};
    var res = { total: 0, fits: [], universal: [], wrongYear: 0, perVehicle: {} };
    vehicles.forEach(function (v) { res.perVehicle[v.id] = 0; });
    rows.forEach(function (r) {
      if (opts.inStock && r[4] === false) return;
      res.total++;
      var p = parsed(r);
      var best = B.fitFeed.best(p, vehicles, r[3]);
      var st = byPipe(best.state, r[2]);
      if (st === 'fits') {
        res.fits.push({ row: r, vehicle: best.vehicle });
        vehicles.forEach(function (v) { if (B.checkFitment(p, v, r[3]) === 'fits') res.perVehicle[v.id]++; });
      } else if (st === 'universal') res.universal.push(r);
      else if (st === 'wrong_year') res.wrongYear++;
    });
    return res;
  }

  // ----- Cards -----
  function handleOf(item) {
    var a = qs('a[href*="/products/"]', item);
    if (!a) return '';
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }
  function originalsOf(grid) {
    var map = {};
    Array.prototype.forEach.call(grid.children, function (el) {
      if (!el.classList.contains('halo-item') || el.hasAttribute('data-bge-kf-added')) return;
      var h = handleOf(el);
      if (h && !map[h]) map[h] = el;
    });
    return map;
  }
  function cardFor(row, originals, view) {
    var orig = originals[row[1]];
    if (orig) {
      var c = orig.cloneNode(true);
      c.removeAttribute('data-bge-kf-hidden');
      c.setAttribute('data-bge-kf-added', '');
      return Promise.resolve(c);
    }
    return B.fitFeed.card(row[1], view).then(function (html) {
      var t = document.createElement('template');
      t.innerHTML = html;
      var item = t.content.querySelector('.halo-item');
      if (!item) return null;
      item.setAttribute('data-bge-kf-added', '');
      return document.importNode(item, true);
    }, function () { return null; });
  }
  // Show the server render again: drop added cards, unhide the originals.
  function restoreGrid(grid) {
    qsa('[data-bge-kf-added]', grid).forEach(function (el) { el.remove(); });
    qsa('[data-bge-kf-hidden]', grid).forEach(function (el) { el.removeAttribute('data-bge-kf-hidden'); });
  }
  function hideOriginals(grid) {
    Array.prototype.forEach.call(grid.children, function (el) {
      if (!el.hasAttribute('data-bge-kf-added')) el.setAttribute('data-bge-kf-hidden', '');
    });
  }
  function afterCards() {
    B.renderBadges();
    try { if (window.theme && window.theme.wishlist && typeof window.theme.wishlist.init === 'function') window.theme.wishlist.init(); } catch (e) { /* optional */ }
  }
  function pendingDone() { document.documentElement.classList.remove('bge-kf-pending'); }

  function btn(action, surface, label) {
    return '<button type="button" class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" data-bge-kf-action="' + action + '" data-surface="' + surface + '">' + esc(label) + '</button>';
  }
  function linkBtn(href, label) {
    return '<a class="bgv2-btn bgv2-btn--secondary bgv2-btn--sm" href="' + esc(href) + '">' + esc(label) + '</a>';
  }

  // ---------------------------------------------------------------------------
  // 1. Home Best Sellers. Source: the collection behind the section's own
  //    "Shop all" link; count: the cards the section rendered.
  //    The fits sit deep in that list (an RZR XP 1000's first fit is rank 155
  //    of 1,134), so the row reads the whole collection index.
  var surfaces = {};

  surfaces.home_best = function () {
    var root = qs('[data-section-type="bgv2-best-sellers"]');
    if (!root) return null;
    var grid = qs('.bgv2-best__grid', root);
    var h2 = qs('.bgv2-best__header h2', root);
    var link = qs('.bgv2-best__header a', root);
    var note = qs('[data-bge-kf-note]', root);
    if (!grid || !h2 || !link || !note) return null;
    var heading = h2.textContent;
    var originals = originalsOf(grid);
    var limit = Object.keys(originals).length;
    var key = new URL(link.getAttribute('href'), window.location.href).pathname;
    var run = 0;

    function reset() {
      restoreGrid(grid);
      qsa('[data-bge-kf-added]', root).forEach(function (el) { el.remove(); });
      root.removeAttribute('data-bge-kf-on');
      h2.textContent = heading;
    }
    function setNote(html) { note.innerHTML = html; note.hidden = !html; }

    var lastSig = null;
    function render() {
      var vs = B.Garage.list();
      var sig = stateSig(vs, 'home_best');
      if (sig === lastSig) return;
      lastSig = sig;
      var token = ++run;
      if (!vs.length || !limit) { reset(); setNote(''); return pendingDone(); }
      var allName = vs.length === 1 ? modelName(vs[0]) : 'machines';
      if (isAll('home_best')) {
        reset();
        setNote(btn('fit', 'home_best', 'Show best sellers for my ' + allName));
        return pendingDone();
      }
      B.fitFeed.index(key).then(function (rows) {
        if (token !== run) return;
        var res = match(rows, vs, { inStock: true });
        if (!res.fits.length) {
          // Zero guard: the default row, no "for your" label, a way to the machine's parts.
          reset();
          setNote('<p class="bge-kf-note__text">None of our best sellers fit your ' + esc(vs.length === 1 ? name(vs[0]) : 'machines') + ' yet.</p>' +
            vs.slice(0, 2).map(function (v) { return linkBtn(shopUrl(v), shopLabel(v)); }).join(''));
          track('known_fit_zero', { surface: 'home_best', machines: vs.length });
          return pendingDone();
        }
        var list = res.fits.map(function (f) { return f.row; }).concat(res.universal).slice(0, limit);
        var withFits = vs.filter(function (v) { return res.perVehicle[v.id] > 0; });
        return Promise.all(list.map(function (r) { return cardFor(r, originals, 'bge-card-grid'); })).then(function (nodes) {
          if (token !== run) return;
          nodes = nodes.filter(Boolean);
          if (!nodes.length) { reset(); setNote(''); return pendingDone(); }
          reset();
          hideOriginals(grid);
          nodes.forEach(function (n) { grid.appendChild(n); });
          root.setAttribute('data-bge-kf-on', '');
          h2.textContent = 'Best sellers for your ' + (withFits.length === 1 ? name(withFits[0]) : 'machines');
          setNote((withFits.length > 1 ? '<p class="bge-kf-note__text">For your ' + esc(joinOr(withFits.map(name))) + '.</p>' : '') +
            btn('all', 'home_best', 'Show all best sellers'));
          // Short row: the truth for this machine, plus the next step.
          if (nodes.length < limit) {
            var more = document.createElement('p');
            more.className = 'bge-kf-more';
            more.setAttribute('data-bge-kf-added', '');
            more.innerHTML = withFits.slice(0, 2).map(function (v) {
              return '<a class="bgv2-textlink" href="' + esc(shopUrl(v)) + '">Shop all parts for your ' + esc(name(v)) + '</a>';
            }).join('');
            grid.parentNode.insertBefore(more, grid.nextSibling);
          }
          afterCards();
          track('known_fit_apply', { surface: 'home_best', machines: withFits.length, results: nodes.length, total: res.total });
          pendingDone();
        });
      }).catch(function (err) {
        if (token !== run) return;
        console.warn('[BGE known fit] Best sellers index failed:', err);
        lastSig = null;
        reset(); setNote(''); pendingDone();
      });
    }
    return { render: render };
  };


  // ---------------------------------------------------------------------------
  // 2. Search results. The whole result set is indexed
  //    (templates/search.bge-fit.liquid), fits then universal parts are shown a
  //    page at a time with Show more, and the theme pagination steps aside.
  surfaces.search = function () {
    if (!/^\/search\/?$/.test(window.location.pathname)) return null;
    var root = qs('.page-search');
    var grid = root && qs('.halo-column-product', root);
    var note = root && qs('[data-bge-kf-note]', root);
    if (!grid || !note) return null;
    var params = new URLSearchParams(window.location.search);
    if (!params.get('q')) return null;
    params.delete('page'); params.delete('view');
    var key = '/search?' + params.toString();
    var originals = originalsOf(grid);
    var pageSize = Math.max(Object.keys(originals).length, 12);
    var html = document.documentElement;
    var run = 0, lastSig = null, list = [], shown = 0;
    // One total on screen (RV2-03): Shopify's search count (the heading and toolbar) can be
    // lower than the products its pages hold (q=exhaust: 1002 against 1,248 in the index), so
    // the note and the toolbar use the server's number, as the heading does.
    var coll = qs('#Collection');
    var serverTotal = coll ? parseInt(coll.getAttribute('data-bgv3-total'), 10) || 0 : 0;
    var toolbar = qs('.bgv3-toolbar__count');
    var toolbarHtml = toolbar ? toolbar.innerHTML : '';
    function totalOf(res) { return serverTotal || res.total; }
    function setToolbar(n, total) {
      if (!toolbar) return;
      var h = n == null ? toolbarHtml : 'Showing <b>' + n + ' of ' + total + '</b> ' + plural(total, 'result');
      if (toolbar.innerHTML !== h) toolbar.innerHTML = h;
    }

    function reset() {
      restoreGrid(grid);
      grid.removeAttribute('data-bge-kf-on');
      html.classList.remove('bge-kf-search-on');
      setToolbar(null);
    }
    function setNote(title, text, actions) {
      if (!title) { note.innerHTML = ''; note.hidden = true; return; }
      note.hidden = false;
      note.innerHTML = '<p class="bge-kf-note__title">' + title + '</p>' +
        (text ? '<p class="bge-kf-note__text">' + text + '</p>' : '') +
        (actions ? '<div class="bge-kf-note__actions">' + actions + '</div>' : '');
    }
    // The filled note can be shorter than the phone reserve (one result, no Show all). Drop the reserve as it
    // fills, but only while the grid is still held unpainted, so nothing on screen moves (FL2-17).
    function settle() { if (html.classList.contains('bge-kf-pending')) note.classList.add('bge-kf-note--fitted'); }
    function addBtn() {
      // all(): a machine saved without a model still takes a Garage slot (the drawer counts it too).
      var count = B.Garage.all ? B.Garage.all().length : B.Garage.list().length;
      return count < 5 ? '<button type="button" class="bgv2-textlink" data-bge-action="open-drawer">Add a machine</button>' : '';
    }

    // before() runs in the same tick as the append, so the grid never sits
    // empty while cards are fetched (no collapse, no layout shift).
    function more(token, before) {
      var slice = list.slice(shown, shown + pageSize), start = shown;
      return Promise.all(slice.map(function (r) { return cardFor(r, originals, 'bge-card-grid'); })).then(function (nodes) {
        if (token !== run) return;
        if (before) before();
        var m = qs('.bge-kf-more-row', grid);
        if (m) m.remove();
        nodes.forEach(function (n) { if (n) grid.appendChild(n); });
        shown = start + slice.length;
        var left = list.length - shown;
        if (left > 0) {
          var row = document.createElement('div');
          var n = Math.min(left, pageSize);
          row.className = 'halo-item bge-kf-more-row';
          row.setAttribute('data-bge-kf-added', '');
          row.innerHTML = '<button type="button" class="bgv2-btn bgv2-btn--secondary" data-bge-kf-action="more" data-surface="search">Show ' + n + ' more ' + plural(n, 'result') + '</button>';
          grid.appendChild(row);
        }
        afterCards();
      });
    }

    function render() {
      var vs = B.Garage.list();
      var sig = stateSig(vs, 'search');
      if (sig === lastSig) return;
      lastSig = sig;
      var token = ++run;
      // No machine with a model (none saved, or only a legacy model-less one): no note will come,
      // so the panel gives back its reserved space (FL2-07).
      if (!vs.length) { reset(); setNote(''); html.classList.remove('bge-kf-has'); return pendingDone(); }
      // While the match runs (the grid is held unpainted), the reserved panel says so: no blank
      // area without a cue (bg-review-2). Same space as the result, so nothing moves.
      if (!isAll('search') && html.classList.contains('bge-kf-pending')) {
        setNote('Checking fit for your ' + esc(vs.length === 1 ? name(vs[0]) : vs.length + ' machines'), '', '');
        note.setAttribute('aria-busy', 'true');
      }
      B.fitFeed.index(key).then(function (rows) {
        if (token !== run) return;
        note.removeAttribute('aria-busy');
        var res = match(rows, vs);
        var withFits = vs.filter(function (v) { return res.perVehicle[v.id] > 0; });
        var mine = vs.length === 1 ? modelName(vs[0]) : 'machines';
        if (isAll('search')) {
          reset();
          setNote('Showing all ' + totalOf(res) + ' ' + plural(totalOf(res), 'result'), '',
            (res.fits.length ? btn('fit', 'search', 'Show results for my ' + mine) : '') + addBtn());
          settle();
          return pendingDone();
        }
        if (!res.fits.length) {
          reset();
          setNote('None of these results fit your ' + esc(vs.length === 1 ? name(vs[0]) : 'machines'),
            res.wrongYear && vs.length === 1 ? res.wrongYear + ' ' + plural(res.wrongYear, 'result') + ' ' + fitVerb(res.wrongYear) + ' other ' + esc(modelName(vs[0])) + ' years.' : '',
            vs.slice(0, 2).map(function (v) { return linkBtn(shopUrl(v), shopLabel(v)); }).join('') + addBtn());
          track('known_fit_zero', { surface: 'search', machines: vs.length });
          settle();
          return pendingDone();
        }
        list = res.fits.map(function (f) { return f.row; }).concat(res.universal);
        shown = 0;
        var n = res.fits.length;
        var notes = [];
        var none = vs.filter(function (v) { return !res.perVehicle[v.id]; });
        if (vs.length > 1 && none.length) notes.push('None fit your ' + esc(joinOr(none.map(name))) + '.');
        if (res.universal.length) notes.push('Plus ' + res.universal.length + ' universal ' + plural(res.universal.length, 'part') + '.');
        if (res.wrongYear) notes.push(res.wrongYear + ' more ' + fitVerb(res.wrongYear) + ' other ' + (vs.length === 1 ? esc(modelName(vs[0])) + ' ' : '') + 'years.');
        return more(token, function () {
          reset();
          hideOriginals(grid);
          grid.setAttribute('data-bge-kf-on', '');
          html.classList.add('bge-kf-search-on');
          // Singular "fits", and Show all only when the scoped list hides something (FL1-51).
          setNote(n + ' ' + plural(n, 'result') + ' ' + fitVerb(n) + ' your ' + esc(withFits.length === 1 ? name(withFits[0]) : withFits.length + ' machines'),
            notes.join(' '),
            (list.length < totalOf(res) ? btn('all', 'search', 'Show all ' + totalOf(res) + ' ' + plural(totalOf(res), 'result')) : '') + addBtn());
          setToolbar(Math.min(list.length, totalOf(res)), totalOf(res));
          settle();
          track('known_fit_apply', { surface: 'search', machines: withFits.length, results: n, universal: res.universal.length, total: res.total });
        }).then(pendingDone, pendingDone);
      }).catch(function (err) {
        if (token !== run) return;
        note.removeAttribute('aria-busy');
        console.warn('[BGE known fit] Search index failed:', err);
        lastSig = null;
        reset(); setNote(''); pendingDone();
      });
    }
    return { render: render, more: function () { return more(run); } };
  };

  // ---------------------------------------------------------------------------
  // 3. PDP Related products. Markup contract with the PDP session (section
  //    bgv2-pdp-related, branch pdp-v3): a root [data-bge-kf="pdp_related"]
  //    carrying the product's own tags and type, cards [data-bge-kf-card] with
  //    data-bge-tags / data-bge-type, slots [data-bge-kf-label] and
  //    [data-bge-kf-toggle]. The section dispatches bgv2:related:ready
  //    {detail:{root}} after every render and sets data-bgv2-ready="1".
  //    Scoped only when the product on screen fits (or is universal for) a
  //    saved machine; otherwise the rider is shopping another machine and the
  //    list stays as it is. Recently viewed cards are badged, never filtered.
  surfaces.pdp_related = function () {
    var OK = { fits: 1, universal: 1 };
    // A card's grid slot is its <li>: hide both, or the row keeps an empty slot (FL1-21).
    function slot(c) { var p = c.parentElement; return p && p.tagName === 'LI' ? p : c; }
    function hide(c, on) {
      [c, slot(c)].forEach(function (el) { if (on) el.setAttribute('data-bge-kf-hidden', ''); else el.removeAttribute('data-bge-kf-hidden'); });
    }
    // FL3-20 (KF-BAND): the label slot is reserved for any saved machine (html.bge-kf-has); a row whose label will not
    // render gives the space back (data-bge-kf-none, bgv2-pdp.css). Decided at boot from the page product, before the
    // cards arrive below the fold, and again once they are in (no related card fits).
    function release(root, on) { if (on) root.setAttribute('data-bge-kf-none', ''); else root.removeAttribute('data-bge-kf-none'); }
    function ownMine(root, vs) {
      var ownTags = root.getAttribute('data-bge-kf-product-tags') || '';
      var own = B.parseTags(ownTags);
      var ownType = root.getAttribute('data-bge-kf-product-type') || '';
      return vs.filter(function (v) { return OK[byPipe(B.checkFitment(own, v, ownType), ownTags)]; });
    }
    function early(root) { var vs = B.Garage.list(); release(root, !vs.length || !ownMine(root, vs).length); }
    function scope(root) {
      var vs = B.Garage.list();
      var sig = stateSig(vs, 'pdp_related');
      if (root._bgeKfSig === sig) return;
      root._bgeKfSig = sig;
      var label = qs('[data-bge-kf-label]', root);
      var toggle = qs('[data-bge-kf-toggle]', root);
      var cards = qsa('[data-bge-kf-card]', root);
      var set = function (l, t) { if (label) label.textContent = l; if (toggle) toggle.innerHTML = t; };
      cards.forEach(function (c) { hide(c, false); });
      root.removeAttribute('data-bge-kf-on');
      set('', '');
      var done = function () { B.renderBadges(); };
      if (!vs.length || !cards.length) { release(root, true); return done(); }
      var mine = ownMine(root, vs);
      release(root, !mine.length);
      if (!mine.length) return done();
      var mineName = mine.length === 1 ? modelName(mine[0]) : 'machines';
      if (isAll('pdp_related')) {
        set('', btn('fit', 'pdp_related', 'Show related for my ' + mineName));
        return done();
      }
      var states = cards.map(function (c) {
        var tags = c.getAttribute('data-bge-tags') || '';
        return byPipe(B.fitFeed.best(B.parseTags(tags), mine, c.getAttribute('data-bge-type') || '').state, tags);
      });
      var keep = cards.filter(function (c, i) { return OK[states[i]]; });
      if (!keep.length) {
        track('known_fit_zero', { surface: 'pdp_related', machines: mine.length });
        release(root, true);
        return done();
      }
      cards.forEach(function (c) { if (keep.indexOf(c) < 0) hide(c, true); });
      root.setAttribute('data-bge-kf-on', '');
      // Universal parts fit by pipe, not by machine, so the label never claims a machine fit for them
      // (FL1-22). Same words as the collection and search notes.
      var nFit = states.filter(function (s) { return s === 'fits'; }).length;
      var nUni = keep.length - nFit;
      var who = mine.length === 1 ? name(mine[0]) : 'machines';
      set(!nFit ? (((window.BGV2 && window.BGV2.strings) || {}).fitUniversal || 'These parts are not machine specific.')
        : !nUni ? 'Fits your ' + who
        : nFit + ' ' + plural(nFit, 'part') + ' ' + fitVerb(nFit) + ' your ' + who + '. Plus ' + nUni + ' universal ' + plural(nUni, 'part') + '.',
        btn('all', 'pdp_related', 'Show all related'));
      track('known_fit_apply', { surface: 'pdp_related', machines: mine.length, results: nFit, universal: nUni, total: cards.length });
      done();
    }
    document.addEventListener('bgv2:related:ready', function (e) {
      var root = e.detail && e.detail.root;
      if (!root || !B) return;
      root._bgeKfSig = null; // new cards
      scope(root);
    });
    return { render: function () {
      qsa('[data-bge-kf="pdp_related"]:not([data-bgv2-ready="1"])').forEach(early);
      qsa('[data-bge-kf="pdp_related"][data-bgv2-ready="1"]').forEach(scope);
    } };
  };

  // ---------------------------------------------------------------------------
  // 4. Mega panel and drawer "Popular machines": the rider's machine moves to
  //    the top of the list with a tick. Links and their text are unchanged;
  //    only the order (client side) and a mark. Not in that class's list: no
  //    change.
  surfaces.mega_machines = function () {
    var lists = qsa('.bgv2-nav-group').filter(function (g) {
      var head = qs('.bgv2-nav-group__head', g);
      return g.classList.contains('bgv2-nav-group--machines') || (head && /popular machines/i.test(head.textContent));
    }).map(function (g) { return qs('.bgv2-nav-group__list', g); }).filter(Boolean);
    if (!lists.length) return null;
    lists.forEach(function (ul) {
      qsa(':scope > li', ul).forEach(function (li, i) {
        li.setAttribute('data-bge-kf-i', i);
        if (li.classList.contains('is-extra')) li.setAttribute('data-bge-kf-extra', '');
      });
    });
    var lastSig = null;
    function render() {
      var vs = B.Garage.list();
      var sig = stateSig(vs, 'mega_machines');
      if (sig === lastSig) return;
      lastSig = sig;
      var urls = vs.map(function (v) { return B.modelUrl(v); });
      lists.forEach(function (ul) {
        var lis = qsa(':scope > li', ul).sort(function (a, b) { return a.getAttribute('data-bge-kf-i') - b.getAttribute('data-bge-kf-i'); });
        lis.forEach(function (li) {
          ul.appendChild(li);
          li.classList.remove('bge-kf-yours');
          li.classList.toggle('is-extra', li.hasAttribute('data-bge-kf-extra'));
          qsa('[data-bge-kf-added]', li).forEach(function (el) { el.remove(); });
        });
        var mine = [];
        urls.forEach(function (u) {
          if (!u) return;
          lis.forEach(function (li) {
            var a = qs('a', li);
            if (a && a.pathname.replace(/\/$/, '') === u && mine.indexOf(li) < 0) mine.push(li);
          });
        });
        mine.slice().reverse().forEach(function (li) { ul.insertBefore(li, ul.firstChild); });
        mine.forEach(function (li) {
          li.classList.add('bge-kf-yours');
          var a = qs('a', li);
          a.insertAdjacentHTML('beforeend', '<span class="bge-kf-tick" data-bge-kf-added aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" focusable="false"><polyline points="20 6 9 17 4 12"/></svg></span><span class="visually-hidden" data-bge-kf-added> (your machine)</span>');
          // Keep the drawer's "Show more" count: a promoted extra swaps with the last visible row.
          if (li.classList.contains('is-extra')) {
            li.classList.remove('is-extra');
            var shown = qsa(':scope > li:not(.is-extra):not(.bge-kf-yours)', ul);
            if (shown.length) shown[shown.length - 1].classList.add('is-extra');
          }
        });
      });
    }
    return { render: render };
  };

  // ---------------------------------------------------------------------------
  // 5. Home Featured models: the rider's machine tile gets a "Yours" tag and
  //    the accent edge. Tiles stay in place, so the grid never moves.
  surfaces.model_tiles = function () {
    var tiles = qsa('.bgv2-models__tile');
    if (!tiles.length) return null;
    var lastSig = null;
    function render() {
      var vs = B.Garage.list();
      var sig = stateSig(vs, 'model_tiles');
      if (sig === lastSig) return;
      lastSig = sig;
      var urls = vs.map(function (v) { return B.modelUrl(v); }).filter(Boolean);
      tiles.forEach(function (t) {
        var li = t.parentNode;
        li.classList.remove('bge-kf-yours');
        qsa('[data-bge-kf-added]', t).forEach(function (el) { el.remove(); });
        if (urls.indexOf(t.pathname.replace(/\/$/, '')) < 0) return;
        li.classList.add('bge-kf-yours');
        var media = qs('.bgv2-models__media', t) || t;
        media.insertAdjacentHTML('beforeend', '<span class="bgv2-tag bgv2-tag--badge bge-kf-yours-tag" data-bge-kf-added>Yours</span>');
      });
    }
    return { render: render };
  };

  // ---------------------------------------------------------------------------
  // 6. Finder (empty search, 404, PDP foot): with a machine saved, a line above
  //    the Garage button goes straight to that machine's parts. Without a
  //    Garage the finder is the server markup. A lead finder (404, FL2-18)
  //    makes the active machine's link the primary, in the Garage bar's words
  //    and link, and the Garage button the secondary.
  surfaces.finder = function () {
    var finders = qsa('[data-bgv2-finder]');
    if (!finders.length) return null;
    var lastSig = null;
    function render() {
      var vs = B.Garage.list();
      var sig = stateSig(vs, 'finder') + '|' + ((B.Garage.active() || {}).id || '');
      if (sig === lastSig) return;
      lastSig = sig;
      finders.forEach(function (f) {
        qsa('[data-bge-kf-added]', f).forEach(function (el) { el.remove(); });
        var at = qs('[data-bge-action="open-drawer"]', f);
        var lead = f.hasAttribute('data-bgv2-finder-lead') && at;
        if (lead) at.classList.remove('bgv2-btn--secondary');
        if (lead) at.classList.add('bgv2-btn--primary');
        if (!vs.length || !at) return;
        var main = lead ? B.Garage.active() : null;
        if (main) {
          var a = document.createElement('a');
          a.className = 'bgv2-btn bgv2-btn--primary';
          a.href = shopUrl(main);
          a.setAttribute('data-bge-kf-added', '');
          a.setAttribute('data-bge-kf-click', 'finder');
          a.textContent = 'Shop parts for your ' + modelName(main);
          at.parentNode.insertBefore(a, at);
          at.classList.remove('bgv2-btn--primary');
          at.classList.add('bgv2-btn--secondary');
          vs = vs.filter(function (v) { return v.id !== main.id; });
          if (!vs.length) return;
        }
        var p = document.createElement('p');
        p.className = 'bge-kf-finder';
        p.setAttribute('data-bge-kf-added', '');
        p.innerHTML = vs.slice(0, 3).map(function (v) {
          return '<a class="bgv2-textlink" href="' + esc(shopUrl(v)) + '" data-bge-kf-click="finder">Shop parts for your ' + esc(name(v)) + '</a>';
        }).join('');
        at.parentNode.insertBefore(p, at);
      });
    }
    return { render: render };
  };

  // ---------------------------------------------------------------------------
  // 7. The machine in the chrome (Desktop journeys audit R5): with a machine
  //    saved, the actions that ask for one go to its parts instead. Hero and
  //    page links to #garage ("Find parts for my machine") and the nav Garage
  //    button become "Shop parts for my Raptor 700" / "Parts for my Raptor 700"
  //    to the model collection; the newsletter heading names the machine. Text
  //    and href only, from Garage.active(); the server markup is restored when
  //    the Garage is empty. The Garage bar link lives in bge-garage.js.
  surfaces.chrome = function () {
    var links = qsa('a[href$="#garage"], a.bgv2-mega__garagebtn').filter(function (a) {
      return !a.closest('[data-bgv2-navgarage], [data-bge-drawer], .bge-garage-bar');
    });
    var news = qs('.bgv2-news__h');
    if (!links.length && !news) return null;
    links.forEach(function (a) {
      var label = qs('span', a) || a;
      a._bgeKf = { href: a.getAttribute('href'), open: a.hasAttribute('data-bgv2-open-garage'), label: label, text: label.textContent };
    });
    if (news) news._bgeKfText = news.textContent;
    var lastSig = null;
    function render() {
      var v = B.Garage.active();
      var sig = stateSig(v ? [v] : [], 'chrome');
      if (sig === lastSig) return;
      lastSig = sig;
      var url = v ? (B.modelUrl(v) || shopUrl(v)) : '';
      links.forEach(function (a) {
        var o = a._bgeKf;
        if (!v) {
          a.setAttribute('href', o.href);
          if (o.open) a.setAttribute('data-bgv2-open-garage', '');
          o.label.textContent = o.text;
          a.removeAttribute('data-bge-kf-click');
          return;
        }
        a.setAttribute('href', url);
        a.removeAttribute('data-bgv2-open-garage');
        a.setAttribute('data-bge-kf-click', a.classList.contains('bgv2-mega__garagebtn') ? 'nav_garage' : 'hero');
        o.label.textContent = (a.classList.contains('bgv2-mega__garagebtn') ? 'Parts for my ' : 'Shop parts for my ') + modelName(v);
      });
      if (news) news.textContent = v ? 'Get alerts for your ' + modelName(v) : news._bgeKfText;
    }
    return { render: render };
  };

  // ---------------------------------------------------------------------------
  // Boot
  function boot(BGE) {
    B = BGE;
    var live = [], byName = {};
    Object.keys(surfaces).forEach(function (k) {
      var s = surfaces[k]();
      if (s) { live.push(s); byName[k] = s; }
    });
    if (!live.length) return pendingDone();
    var renderAll = function () { live.forEach(function (s) { s.render(); }); };
    renderAll();
    B.Garage.onChange(renderAll);

    // Clicks on a mark or a finder line: which surfaces carry riders to their machine.
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest && e.target.closest('.bge-kf-yours a, a.bgv2-models__tile, [data-bge-kf-click]');
      if (!t || (t.matches('a.bgv2-models__tile') && !t.parentNode.classList.contains('bge-kf-yours'))) return;
      track('known_fit_click', { surface: t.getAttribute('data-bge-kf-click') || (t.closest('.bgv2-models__item') ? 'model_tiles' : 'mega_machines') });
    }, true);

    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest && e.target.closest('[data-bge-kf-action]');
      if (!el) return;
      e.preventDefault();
      var surface = el.getAttribute('data-surface');
      var action = el.getAttribute('data-bge-kf-action');
      if (action === 'more') {
        el.setAttribute('aria-busy', 'true');
        if (byName[surface] && byName[surface].more) byName[surface].more();
        return;
      }
      if (action === 'all') {
        setAll(surface, true);
        track('known_fit_show_all', { surface: surface });
      } else if (action === 'fit') {
        setAll(surface, false);
      }
      renderAll();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { whenGarage(boot); });
  else whenGarage(boot);
  // If the Garage never boots, never leave a list hidden.
  setTimeout(pendingDone, 4000);
})();
